import React, { useMemo } from 'react';
import { StyleSheet, ActivityIndicator, ViewStyle, TextStyle } from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'outline';
export type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  style?: ViewStyle;
  textStyle?: TextStyle;
}

/**
 * Reusable button component with marketplace design system variants
 */
export const Button: React.FC<ButtonProps> = ({
  title,
  onPress,
  variant = 'primary',
  size = 'medium',
  isLoading = false,
  disabled = false,
  fullWidth = false,
  style,
  textStyle,
}) => {
  const mc = useMarketplaceColors();
  const isDisabled = disabled || isLoading;

  const styles = useMemo(() => StyleSheet.create({
    container: { borderRadius: 8, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 8 },
    fullWidth: { width: '100%' },
    text: { fontWeight: '500', textAlign: 'center' },
    primaryContainer: { backgroundColor: mc.accentBlueSolid },
    primaryDisabled: { backgroundColor: mc.buttonDisabled },
    primaryText: { color: mc.textPrimary },
    primaryTextDisabled: { color: mc.buttonDisabledText },
    secondaryContainer: { backgroundColor: 'transparent', borderWidth: 1, borderColor: mc.accentBlueSolid },
    secondaryDisabled: { borderColor: mc.buttonDisabled },
    secondaryText: { color: mc.accentBlue },
    secondaryTextDisabled: { color: mc.textMuted },
    dangerContainer: { backgroundColor: mc.accentRed },
    dangerDisabled: { backgroundColor: mc.buttonDisabled },
    dangerText: { color: mc.background },
    dangerTextDisabled: { color: mc.buttonDisabledText },
    outlineContainer: { backgroundColor: 'transparent', borderWidth: 1, borderColor: mc.textPrimary },
    outlineDisabled: { borderColor: mc.buttonDisabled },
    outlineText: { color: mc.textPrimary },
    outlineTextDisabled: { color: mc.textMuted },
  }), [mc]);

  const getContainerStyle = (): ViewStyle[] => {
    const baseStyle: ViewStyle[] = [styles.container, sizeStyles[size].container];
    if (fullWidth) baseStyle.push(styles.fullWidth);
    switch (variant) {
      case 'primary':
        baseStyle.push(styles.primaryContainer);
        if (isDisabled) baseStyle.push(styles.primaryDisabled);
        break;
      case 'secondary':
        baseStyle.push(styles.secondaryContainer, borderedPadding[size]);
        if (isDisabled) baseStyle.push(styles.secondaryDisabled);
        break;
      case 'danger':
        baseStyle.push(styles.dangerContainer);
        if (isDisabled) baseStyle.push(styles.dangerDisabled);
        break;
      case 'outline':
        baseStyle.push(styles.outlineContainer, borderedPadding[size]);
        if (isDisabled) baseStyle.push(styles.outlineDisabled);
        break;
    }
    if (style) baseStyle.push(style);
    return baseStyle;
  };

  const getTextStyle = (): TextStyle[] => {
    const baseStyle: TextStyle[] = [styles.text, sizeStyles[size].text];
    switch (variant) {
      case 'primary':
        baseStyle.push(styles.primaryText);
        if (isDisabled) baseStyle.push(styles.primaryTextDisabled);
        break;
      case 'secondary':
        baseStyle.push(styles.secondaryText);
        if (isDisabled) baseStyle.push(styles.secondaryTextDisabled);
        break;
      case 'danger':
        baseStyle.push(styles.dangerText);
        if (isDisabled) baseStyle.push(styles.dangerTextDisabled);
        break;
      case 'outline':
        baseStyle.push(styles.outlineText);
        if (isDisabled) baseStyle.push(styles.outlineTextDisabled);
        break;
    }
    if (textStyle) baseStyle.push(textStyle);
    return baseStyle;
  };

  const getLoadingColor = (): string => {
    switch (variant) {
      case 'primary': return mc.textPrimary;
      case 'secondary': return mc.accentBlueSolid;
      case 'danger': return mc.background;
      case 'outline': return mc.textPrimary;
      default: return mc.textPrimary;
    }
  };

  return (
    <Hoverable
      hover={variant === 'primary' || variant === 'danger' ? 'button' : 'row'}
      style={getContainerStyle()}
      onPress={onPress}
      disabled={isDisabled}
    >
      {isLoading ? (
        <ActivityIndicator size="small" color={getLoadingColor()} />
      ) : (
        <Text style={getTextStyle()} numberOfLines={2}>{title}</Text>
      )}
    </Hoverable>
  );
};

// Size configurations (no colors, stays static). Heights are floors: padding
// reproduces the old fixed height at 1x, the button grows at large text sizes.
const sizeStyles = {
  small: StyleSheet.create({ container: { minHeight: 32, paddingVertical: 7, paddingHorizontal: 12 }, text: { fontSize: 12, lineHeight: 18 } }),
  medium: StyleSheet.create({ container: { minHeight: 40, paddingVertical: 9.5, paddingHorizontal: 16 }, text: { fontSize: 14, lineHeight: 21 } }),
  large: StyleSheet.create({ container: { minHeight: 48, paddingVertical: 12, paddingHorizontal: 24 }, text: { fontSize: 16, lineHeight: 24 } }),
};

// The bordered variants (secondary, outline) carry a 1pt border inside the
// box, so their padding gives that 2pt back to stay on the same floor at 1x.
const borderedPadding: Record<ButtonSize, ViewStyle> = {
  small: { paddingVertical: 6 },
  medium: { paddingVertical: 8.5 },
  large: { paddingVertical: 11 },
};
